import useAmplitude from '@eversports/amplitude-react/useAmplitude'
import useLogAmplitudeEventOnce from '@eversports/amplitude-react/useLogAmplitudeEventOnce'
import { boxShadows } from '@eversports/design-tokens/box-shadows'
import Box from '@eversports/klimt-primitives/legacy/Box'
import Heading from '@eversports/klimt-primitives/legacy/Heading'
import SeoLink from '@eversports/klimt-primitives/legacy/SeoLink'
import Stack from '@eversports/klimt-primitives/legacy/Stack'
import Svg from '@eversports/klimt-primitives/legacy/Svg'
import Text from '@eversports/klimt-primitives/legacy/Text'
import { useEffect } from 'react'

import { Localized } from '../../../../localization/react'
import ArrowRightSvg from '../../assets/arrow-right-icon.svg'
import SectionWrapper from '../../components/SectionWrapper'

import { VoucherGiftAnimation } from './VoucherGiftAnimation'
import { VoucherLineAnimation } from './VoucherLineAnimation'

interface Props {
  name: string
  voucherWidgetUrl: string | null
  hasMultipleBranches: boolean
}

export function VenueVouchersBanner({ name, voucherWidgetUrl, hasMultipleBranches }: Props) {
  const { amplitude } = useAmplitude()
  const { logAmplitudeEventOnce } = useLogAmplitudeEventOnce()

  useEffect(() => {
    if (!amplitude) return
    logAmplitudeEventOnce({
      eventName: 'Viewed Voucher Tab',
      eventProperties: { venue: { name }, hasVoucherWidgetUrl: Boolean(voucherWidgetUrl) },
    })
  }, [amplitude, voucherWidgetUrl])

  if (!voucherWidgetUrl) {
    return (
      <Box
        sx={{ position: 'relative', width: '100%', overflowX: 'hidden', paddingBottom: 32, paddingX: { xs: 4, lg: 0 } }}
      >
        <SectionWrapper sx={{ borderColor: 'mediumgray', boxShadow: boxShadows.default, paddingX: 8, paddingY: 7 }}>
          <Text>
            <Localized id="venue-vouchers-banner-empty" />
          </Text>
        </SectionWrapper>
      </Box>
    )
  }

  const handleClickBuyVoucher = () => {
    if (!amplitude) return
    amplitude.logEvent('Clicked Voucher Widget Button', { venue: { name } })
  }

  return (
    <>
      <VoucherLineAnimation />
      <Box
        sx={{ position: 'relative', width: '100%', overflowX: 'hidden', paddingBottom: 32, paddingX: { xs: 4, lg: 0 } }}
      >
        <SectionWrapper
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column-reverse', md: 'row' },
            justifyContent: { xs: 'center', md: 'space-between' },
            alignItems: 'center',
            gap: 4,
            borderColor: 'mediumgray',
            boxShadow: boxShadows.default,
            paddingX: 8,
            paddingY: 7,
          }}
        >
          <Stack gap={4} alignItems="flex-start">
            <Stack gap={2}>
              <Heading is="h1" color="midnight">
                <Localized id="venue-vouchers-banner-title" />
              </Heading>
              <Text>
                <Localized id="venue-vouchers-banner-description" params={{ venueName: name }} />
                {hasMultipleBranches && (
                  <>
                    {' '}
                    <Localized id="voucher-valid-at-all-branches" />
                  </>
                )}
              </Text>
            </Stack>
            <Box sx={{ width: 'fit-content' }} onClick={handleClickBuyVoucher}>
              <SeoLink
                encodedTo={btoa(voucherWidgetUrl)}
                external
                type="hidden"
                buttonVariant="primary"
                dataTestId="venue-vouchers-banner-cta"
              >
                <Stack direction="row" alignItems="center" gap={2}>
                  <Text color="white" variant="small" sx={{ textTransform: 'none' }}>
                    <Localized id="venue-vouchers-banner-cta" />
                  </Text>
                  <Svg src={ArrowRightSvg} width={12} height={12} color="white" />
                </Stack>
              </SeoLink>
            </Box>
          </Stack>
          <VoucherGiftAnimation />
        </SectionWrapper>
      </Box>
    </>
  )
}
